/**
 * GuestDatabase Component
 * Feature: Guests - Real data from Supabase
 */

import { useState } from "react";
import { Search, User, ChevronDown, Building2, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useGuests, useGuestStats } from "../hooks";
import type { Ospite } from "@/types";

interface GuestDatabaseProps {
  onAddGuest?: () => void;
}

export function GuestDatabase({ onAddGuest }: GuestDatabaseProps) {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("Tutti");
  const [selectedGuest, setSelectedGuest] = useState<Ospite | null>(null);
  
  const { data: guests = [], isLoading } = useGuests(search);
  const { data: stats } = useGuestStats();

  const filtered = guests.filter((g: Ospite) => {
    if (filterType !== "Tutti" && g.tipo !== filterType) return false;
    return true;
  });

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-40 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="flex gap-3">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Anagrafica ospiti</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{stats?.total || 0} ospiti registrati</p>
        </div>
        <button 
          onClick={onAddGuest} 
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" /> Nuovo ospite
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input 
            className="w-full border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30" 
            placeholder="Cerca per nome, email..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
          />
        </div>
        <div className="relative">
          <select 
            value={filterType} 
            onChange={e => setFilterType(e.target.value)}
            className="appearance-none border border-border rounded-xl pl-3.5 pr-8 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option>Tutti</option>
            <option>Privato</option>
            <option>Azienda</option>
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Guest List */}
      {filtered.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <User className="h-10 w-10 text-border mx-auto mb-3" />
          <p className="text-muted-foreground">Nessun ospite trovato.</p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border divide-y divide-border">
          {filtered.map((g) => (
            <div 
              key={g.id} 
              className="px-5 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors cursor-pointer"
              onClick={() => setSelectedGuest(g)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground">{g.nome} {g.cognome}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {[g.email, g.telefono, g.cittadinanza].filter(Boolean).join(" · ")}
                  </div>
                  {g.tipo === "Azienda" && g.ragione_sociale && (
                    <div className="text-xs text-primary mt-0.5">
                      <Building2 className="h-3 w-3 inline mr-1" />
                      {g.ragione_sociale}
                    </div>
                  )}
                </div>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground rotate-[-90deg]" />
            </div>
          ))}
        </div>
      )}

      {/* Guest Detail Modal - Simplified */}
      {selectedGuest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-2xl border border-border shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Dettaglio ospite</h2>
              <button 
                onClick={() => setSelectedGuest(null)} 
                className="p-1 rounded-lg hover:bg-slate-100"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-foreground">
                    {selectedGuest.nome} {selectedGuest.cognome}
                  </div>
                  {selectedGuest.tipo === "Azienda" && selectedGuest.ragione_sociale && (
                    <div className="text-sm text-muted-foreground">
                      <Building2 className="h-3.5 w-3.5 inline mr-1" />
                      {selectedGuest.ragione_sociale}
                    </div>
                  )}
                </div>
              </div>
              
              {selectedGuest.email && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Email</label>
                  <div className="text-sm text-foreground">{selectedGuest.email}</div>
                </div>
              )}
              {selectedGuest.telefono && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Telefono</label>
                  <div className="text-sm text-foreground">{selectedGuest.telefono}</div>
                </div>
              )}
              {selectedGuest.cittadinanza && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Cittadinanza</label>
                  <div className="text-sm text-foreground">{selectedGuest.cittadinanza}</div>
                </div>
              )}
              {selectedGuest.nazionalita && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Nazionalità</label>
                  <div className="text-sm text-foreground">{selectedGuest.nazionalita}</div>
                </div>
              )}
              {selectedGuest.note && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Note</label>
                  <div className="text-sm text-foreground">{selectedGuest.note}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
